import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FILTERS_EVENT, FILTERS_STORAGE_KEY } from '../../constants/filters';

const FilterConfigContext = createContext(null);

export const useFilterConfig = () => {
  const ctx = useContext(FilterConfigContext);
  if (!ctx) {
    return {
      mode: null,
      categories: [],
      // No-op fallbacks so consumers can render outside the provider.
      setFilterConfig: () => undefined,
      resetFilterConfig: () => undefined,
    };
  }
  return ctx;
};

const clearAppliedFilters = () => {
  try {
    sessionStorage.removeItem(FILTERS_STORAGE_KEY);
  } catch { /* sessionStorage unavailable */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FILTERS_EVENT, { detail: [] }));
  }
};

export const FilterConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({ mode: null, categories: [] });

  const setFilterConfig = useCallback(({ mode, categories }) => {
    const nextMode = mode ?? null;
    setConfig((prev) => {
      if (prev.mode && nextMode && prev.mode !== nextMode) {
        clearAppliedFilters();
      }
      return {
        mode: nextMode,
        categories: Array.isArray(categories) ? categories : [],
      };
    });
  }, []);

  const resetFilterConfig = useCallback(() => {
    clearAppliedFilters();
    setConfig({ mode: null, categories: [] });
  }, []);

  const value = useMemo(
    () => ({ ...config, setFilterConfig, resetFilterConfig }),
    [config, setFilterConfig, resetFilterConfig],
  );

  return (
    <FilterConfigContext.Provider value={value}>
      {children}
    </FilterConfigContext.Provider>
  );
};
